import { Button } from '@/components/ui/button';
import { cloneElement, useMemo, useState, useEffect } from 'react';
import { Settings, Info, Smartphone, Tablet, Monitor, MonitorOff, Wifi, WifiOff, MoreVertical } from 'lucide-react';
import { ThemeSwitcher } from '@/components/ThemeSwitcher';
import { UserMenu } from '@/components/layout/UserMenu';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { ReactNode } from 'react';
import { NavLink } from '@/components/NavLink';
import { PreviewDevice, usePreviewDevice } from '@/hooks/usePreviewDevice';
import { useIsMobile } from '@/hooks/use-mobile';
import { useInternetStatus } from '@/hooks/useInternetStatus';
import { MesaLogo } from './MesaLogo';
import { usePaulAccess } from '@/lib/paulAccess';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

type HeaderNavItem = {
  id: string;
  label: string;
  icon?: ReactNode;
  to?: string;
};

interface HeaderProps {
  onToggleSettings?: () => void;
  showSettings?: boolean;
  navItems?: HeaderNavItem[];
  activeSection?: string;
  onNavigate?: (sectionId: string) => void;
  widgetSyncState?: 'all-linked' | 'all-unlinked' | 'partial';
  onToggleAllWidgets?: (checked: boolean) => void;
  onProfileClick?: () => void;
  onSignOut?: () => void;
}

export function Header({
  onToggleSettings,
  showSettings = false,
  navItems = [],
  activeSection,
  onNavigate,
  widgetSyncState,
  onToggleAllWidgets,
  onProfileClick,
  onSignOut,
}: HeaderProps) {
  const [syncEnabled, setSyncEnabled] = useState(true);
  const [developerUnlocked, setDeveloperUnlocked] = useState(false);
  const { previewDevice, setPreviewDevice } = usePreviewDevice();
  const isMobile = useIsMobile();
  const isOnline = useInternetStatus();
  const { isPaul } = usePaulAccess();

  // Preview-layout context is managed globally in App.
  const showViewLayoutControls = isPaul && !isMobile;

  useEffect(() => {
    try {
      const disabled = typeof window !== 'undefined' && window.localStorage?.getItem('disableFirestore') === '1';
      setSyncEnabled(!disabled);
    } catch {
      setSyncEnabled(true);
    }
  }, []);

  useEffect(() => {
    setDeveloperUnlocked(isPaul);
  }, [isPaul]);

  const canSeeDeveloper = useMemo(() => Boolean(isPaul), [isPaul]);

  const visibleNavItems = useMemo(
    () => navItems.filter((item) => item.id !== 'developer' || canSeeDeveloper),
    [navItems, canSeeDeveloper]
  );

  const setDevice = (next: PreviewDevice) => {
    setPreviewDevice(next);
  };

  const deviceButtonClass = (active: boolean) =>
    cn(
      'h-8 w-8',
      active ? 'bg-secondary' : 'hover:bg-muted/40'
    );

  const toggleFirestore = (nextChecked?: boolean) => {
    try {
      const next = typeof nextChecked === 'boolean' ? nextChecked : !syncEnabled;
      if (!next) {
        window.localStorage.setItem('disableFirestore', '1');
      } else {
        window.localStorage.removeItem('disableFirestore');
      }
      setSyncEnabled(next);
      window.alert('Firestore toggle updated. Please reload the page for changes to take effect.');
      try {
        window.dispatchEvent(new Event('storage'));
      } catch {
        // ignore
      }
    } catch (err) {
      console.error('Failed to toggle Firestore flag', err);
    }
  };
  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-transparent sm:bg-background/80 sm:backdrop-blur-lg sm:border-b sm:border-border/50">
      <div className="mx-auto w-full px-3 sm:px-4" style={{ paddingTop: 'var(--safe-area-top, 0px)' }}>
        <div className="flex items-center gap-3 sm:gap-4 rounded-b-[28px] border border-white/10 bg-background/85 backdrop-blur-2xl shadow-[0_12px_30px_rgba(0,0,0,0.35)] px-3 py-3 min-h-[60px] sm:min-h-[72px] sm:rounded-none sm:border-0 sm:bg-transparent sm:backdrop-blur-none sm:shadow-none sm:px-0 sm:py-4">
          {/* Logo - always visible, larger on mobile */}
          <NavLink to="/" className="flex items-center flex-shrink-0" aria-label="Thirteen home logo">
            <MesaLogo className="h-12 w-12 sm:h-10 sm:w-10" />
          </NavLink>

          <div className="flex flex-col sm:hidden">
            <span className="text-sm font-display font-semibold tracking-wide text-foreground">THIRTEEN</span>
            <span className="text-[11px] text-muted-foreground -mt-0.5">Galactic Calendar</span>
          </div>

          {/* Text branding - desktop only */}
          <div className="hidden sm:flex flex-shrink-0 flex-col">
            <NavLink to="/" className="block">
              <h1 className="text-lg sm:text-2xl font-display font-bold text-cosmic-gradient leading-none">
                THIRTEEN
              </h1>
            </NavLink>
            <p className="text-xs sm:text-sm text-muted-foreground -mt-0.5">
              Galactic Calendar
            </p>
          </div>

          <div className="ml-auto flex items-center gap-2 sm:hidden">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreVertical className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Sync</span>
                    <Switch
                      checked={widgetSyncState !== 'all-unlinked'}
                      onCheckedChange={(checked) => {
                        if (typeof onToggleAllWidgets === 'function') {
                          onToggleAllWidgets(checked);
                        } else {
                          toggleFirestore(checked);
                        }
                      }}
                      className={widgetSyncState === 'all-unlinked' ? 'bg-red-500' : widgetSyncState === 'partial' ? 'data-[state=checked]:bg-yellow-500' : 'data-[state=checked]:bg-green-500'}
                    />
                  </div>
                </div>
                <DropdownMenuItem onClick={onToggleSettings} className="flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  <span>Settings</span>
                </DropdownMenuItem>
                <DropdownMenuItem className="flex items-center gap-2">
                  {isOnline ? (
                    <>
                      <Wifi className="w-4 h-4 text-green-500" />
                      <span>Connected</span>
                    </>
                  ) : (
                    <>
                      <WifiOff className="w-4 h-4 text-red-500" />
                      <span>Disconnected</span>
                    </>
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem className="flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  <span>Info</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>



          <div className="hidden sm:flex ml-auto items-center gap-2">
            {showViewLayoutControls && (
              <div className="hidden sm:flex items-center gap-1 rounded-full border border-border/50 bg-card/40 px-1 py-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className={deviceButtonClass(previewDevice === 'auto')}
                  onClick={() => setDevice('auto')}
                  title="View layout: Full"
                >
                  <MonitorOff className="w-4 h-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className={deviceButtonClass(previewDevice === 'iphone-15')}
                  onClick={() => setDevice('iphone-15')}
                  title="View layout: Mobile"
                >
                  <Smartphone className="w-4 h-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className={deviceButtonClass(previewDevice === 'ipad-air-5th-gen')}
                  onClick={() => setDevice('ipad-air-5th-gen')}
                  title="View layout: Pad"
                >
                  <Tablet className="w-4 h-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className={deviceButtonClass(previewDevice === 'desktop-1440p')}
                  onClick={() => setDevice('desktop-1440p')}
                  title="View layout: Desktop"
                >
                  <Monitor className="w-4 h-4" />
                </Button>
              </div>
            )}
            <ThemeSwitcher />
            <div className="flex items-center gap-2 px-1">
              <span className="text-xs text-muted-foreground">Sync</span>
              <Switch
                checked={widgetSyncState !== 'all-unlinked'}
                onCheckedChange={(checked) => {
                  if (typeof onToggleAllWidgets === 'function') {
                    onToggleAllWidgets(checked);
                  } else {
                    toggleFirestore(checked);
                  }
                }}
                className={widgetSyncState === 'all-unlinked' ? 'bg-red-500' : widgetSyncState === 'partial' ? 'data-[state=checked]:bg-yellow-500' : 'data-[state=checked]:bg-green-500'}
              />
            </div>
            {onToggleSettings && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onToggleSettings}
                className={showSettings ? 'bg-secondary' : ''}
              >
                <Settings className="w-5 h-5" />
              </Button>
            )}
            <Button variant="ghost" size="icon" title={isOnline ? 'Internet connected' : 'Internet disconnected'}>
              {isOnline ? (
                <Wifi className="w-5 h-5 text-green-500" />
              ) : (
                <WifiOff className="w-5 h-5 text-red-500" />
              )}
            </Button>
            <Button variant="ghost" size="icon">
              <Info className="w-5 h-5" />
            </Button>
            <UserMenu onProfileClick={onProfileClick} onSignOut={onSignOut} />
          </div>
      </div>
      </div>

      {visibleNavItems.length > 0 && (
        <nav className="fixed bottom-0 left-0 right-0 z-50 bg-background/90 backdrop-blur-lg border-t border-border/50">
          <div className="container mx-auto px-4 py-2" style={{ paddingBottom: 'max(0.5rem, var(--safe-area-bottom, 0px))' }}>
            <div className="grid grid-cols-4 gap-1.5 md:grid-cols-6 lg:grid-cols-8">
              {/* Logo as first item on mobile */}
              <NavLink
                to="/"
                className="flex flex-col items-center justify-center gap-1 rounded-xl border border-transparent text-xs transition text-muted-foreground hover:text-foreground hover:border-border/70 hover:bg-muted/40 min-h-[40px] min-w-[40px] px-0 py-0"
                aria-label="Thirteen home"
              >
                <MesaLogo className="h-7 w-7" />
              </NavLink>
              
              {visibleNavItems.slice(0, isMobile ? 3 : 5).map((item) =>
                item.to ? (
                  <NavLink
                    key={item.id}
                    to={item.to}
                    className={cn(
                      'flex flex-col items-center justify-center gap-1 rounded-xl border border-transparent text-xs transition',
                      'text-muted-foreground hover:text-foreground hover:border-border/70 hover:bg-muted/40',
                      'min-h-[40px] min-w-[40px] px-0 py-0'
                    )}
                    activeClassName="bg-primary/15 text-primary shadow-[0_0_0_1px_rgba(255,255,255,0.05)] border-primary/30"
                  >
                    <span className="leading-none flex items-center justify-center" style={{height:22, width:22}}>
                      {item.icon && typeof item.icon === 'object' && 'type' in item.icon
                        ? cloneElement(item.icon as React.ReactElement, { className: 'w-5 h-5' })
                        : item.icon}
                    </span>
                  </NavLink>
                ) : (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => onNavigate?.(item.id)}
                    className={cn(
                      'flex flex-col items-center justify-center gap-1 rounded-xl border border-transparent text-xs transition',
                      activeSection === item.id
                        ? 'bg-primary/15 text-primary shadow-[0_0_0_1px_rgba(255,255,255,0.05)] border-primary/30'
                        : 'text-muted-foreground hover:text-foreground hover:border-border/70 hover:bg-muted/40',
                      'min-h-[40px] min-w-[40px] px-0 py-0'
                    )}
                  >
                    <span className="leading-none flex items-center justify-center" style={{height:22, width:22}}>
                      {item.icon && typeof item.icon === 'object' && 'type' in item.icon
                        ? cloneElement(item.icon as React.ReactElement, { className: 'w-5 h-5' })
                        : item.icon}
                    </span>
                  </button>
                )
              )}

              {/* Dropdown menu for overflow items */}
              {visibleNavItems.length > (isMobile ? 3 : 5) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className={cn(
                      'flex flex-col items-center justify-center gap-1 rounded-xl border border-transparent text-xs transition',
                      'text-muted-foreground hover:text-foreground hover:border-border/70 hover:bg-muted/40',
                      'min-h-[40px] min-w-[40px] px-0 py-0'
                    )}>
                      <span className="leading-none flex items-center justify-center" style={{height:22, width:22}}>
                        <MoreVertical className="w-5 h-5" />
                      </span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="mb-16">
                    {visibleNavItems.slice(isMobile ? 3 : 5).map((item) =>
                      item.to ? (
                        <NavLink key={item.id} to={item.to} className="w-full">
                          <DropdownMenuItem className="flex items-center gap-2 cursor-pointer">
                            {item.icon && typeof item.icon === 'object' && 'type' in item.icon
                            ? cloneElement(item.icon as React.ReactElement, { className: 'w-4 h-4' })
                              : item.icon}
                            <span>{item.label}</span>
                          </DropdownMenuItem>
                        </NavLink>
                      ) : (
                        <DropdownMenuItem
                          key={item.id}
                          onClick={() => onNavigate?.(item.id)}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          {item.icon && typeof item.icon === 'object' && 'type' in item.icon
                            ? cloneElement(item.icon as React.ReactElement, { className: 'w-4 h-4' })
                            : item.icon}
                          <span>{item.label}</span>
                        </DropdownMenuItem>
                      )
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </nav>
      )}
    </header>
  );
}
